import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { fetchVoiceNotes, deleteVoiceNote, voiceNoteAudioUrl } from '../../api';
import './VoiceNotesPanel.css';

const POLL_MS = 10000;

const SOURCE_LABEL = { camera: 'CAM', phone: 'PHONE', watch: 'WATCH' };
const STATUS_LABEL = {
  pending: 'Transcribing',
  done: '',
  failed: 'Could not transcribe',
  skipped: 'No speech found',
};

function formatDuration(seconds) {
  const s = Math.round(Number(seconds) || 0);
  if (!s) return '';
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Voice notes of the selected photo's match: play, read the transcript,
 * insert it into the caption. Notes bound to THIS frame are listed first and
 * tagged "this frame"; the rest are match notes (grid or watch memos) and
 * show on every frame of the match, tagged "match". Nothing is written into
 * a caption until Insert is pressed on the frame it belongs to.
 */
export default function VoiceNotesPanel({ matchId, imageId, onInsert }) {
  // Keyed on the match so a switch never shows the previous match's notes
  // while the first fetch is in flight (no synchronous reset in an effect).
  const [loaded, setLoaded] = useState({ matchId: null, rows: [] });
  const [error, setError] = useState(null);
  const notes = loaded.matchId === matchId ? loaded.rows : [];

  useEffect(() => {
    if (!matchId) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const rows = await fetchVoiceNotes(matchId);
        if (cancelled) return;
        setLoaded({ matchId, rows });
        setError(null);
      } catch {
        if (!cancelled) setError('Could not load voice notes');
      }
    };
    tick();
    const timer = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [matchId]);

  if (!matchId) return null;

  // A camera memo is ALWAYS about one frame, so it shows on that frame only:
  // listing another frame's memo here put the same two notes on all 363 photos
  // of the match (field-found 16-09-2026). Only a memo with no photo at all --
  // recorded from the grid or the watch -- is a match note.
  const forFrame = notes.filter((n) => n.photo === imageId);
  const forMatch = notes.filter((n) => !n.photo);
  const ordered = [...forFrame, ...forMatch];
  if (!ordered.length && !error) return null;

  const handleDelete = async (note) => {
    try {
      await deleteVoiceNote(note.id);
      setLoaded((prev) => ({ ...prev, rows: prev.rows.filter((n) => n.id !== note.id) }));
    } catch {
      setError('Could not delete the note');
    }
  };

  return (
    <div className="voice-notes-panel" data-testid="voice-notes-panel">
      <div className="voice-notes-panel__header">
        <span className="voice-notes-panel__title">VOICE NOTES</span>
        <span className="voice-notes-panel__count">{ordered.length}</span>
      </div>
      {error && <div className="voice-notes-panel__error">{error}</div>}
      <ul className="voice-notes-panel__list">
        {ordered.map((note) => {
          const status = STATUS_LABEL[note.transcript_status] ?? '';
          const isFrame = note.photo === imageId;
          return (
            <li key={note.id} className={`voice-note${isFrame ? ' voice-note--frame' : ''}`}>
              <div className="voice-note__meta">
                <span className="voice-note__source">{SOURCE_LABEL[note.source] || note.source}</span>
                <span className="voice-note__who">{note.user_name || 'unknown'}</span>
                <span className="voice-note__time">{formatTime(note.recorded_at)}</span>
                {note.duration_s ? <span className="voice-note__dur">{formatDuration(note.duration_s)}</span> : null}
                <span className="voice-note__frame">{isFrame ? 'this frame' : 'match'}</span>
              </div>
              <audio className="voice-note__audio" controls preload="none" src={voiceNoteAudioUrl(note.id)} />
              {note.transcript ? (
                <p className="voice-note__transcript">{note.transcript}</p>
              ) : (
                <p className={`voice-note__status voice-note__status--${note.transcript_status}`}>{status}</p>
              )}
              <div className="voice-note__actions">
                <button
                  type="button"
                  className="voice-note__btn"
                  disabled={!note.transcript || !onInsert}
                  onClick={() => onInsert?.(note.transcript)}
                  title="Insert the transcript at the caret in the caption, else in the persons slot"
                >
                  Insert
                </button>
                <button type="button" className="voice-note__btn voice-note__btn--quiet" onClick={() => handleDelete(note)}>
                  Delete
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

VoiceNotesPanel.propTypes = {
  matchId: PropTypes.string,
  imageId: PropTypes.string,
  onInsert: PropTypes.func,
};
